import { useEffect, useState } from "react";
import { db } from "./firebase";
import {
  collection, onSnapshot, query, where, doc, setDoc, updateDoc, deleteDoc, getDocs,
} from "firebase/firestore";
import { inputStyle, btnStyle, btnSecondaryStyle, cardStyle, labelStyle, colors } from "./lib/theme";
import { dateKey, formatDayTime, formatDate } from "./lib/weekUtils";
import SocialSession from "./SocialSession";
import LeagueSession from "./LeagueSession";

export default function LiveSessionPage({ user, template, occurrenceDate, onExit }) {
  var occKey = dateKey(occurrenceDate);
  var sessionId = occKey + "_" + template.id;

  var [live, setLive] = useState(undefined); // undefined = loading, null = not created yet
  var [voters, setVoters] = useState([]);
  var [allUsers, setAllUsers] = useState([]);
  var [allDependents, setAllDependents] = useState([]);

  // setup form state
  var [mode, setMode] = useState("social");
  var [courtCount, setCourtCount] = useState(2);
  var [roster, setRoster] = useState({}); // uid -> true/false
  var [addUid, setAddUid] = useState("");

  useEffect(function () {
    var unsub = onSnapshot(doc(db, "liveSessions", sessionId), function (snap) {
      setLive(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    });
    return unsub;
  }, [sessionId]);

  useEffect(function () {
    onSnapshot(query(collection(db, "pollVotes"), where("occurrenceDate", "==", occKey), where("templateId", "==", template.id)), function (snap) {
      var vs = snap.docs.map(function (d) { return d.data(); });
      setVoters(vs);
      setRoster(function (prev) {
        var next = { ...prev };
        vs.forEach(function (v) { if (!(v.uid in next)) next[v.uid] = true; });
        return next;
      });
    });
    getDocs(collection(db, "users")).then(function (snap) {
      setAllUsers(snap.docs.map(function (d) { return { uid: d.id, ...d.data() }; }));
    });
    getDocs(collection(db, "dependents")).then(function (snap) {
      // A "linked" dependent stands in for a real account (its own uid), not
      // the dependent doc's own id — resolve that so it isn't treated as a
      // separate identity from their real profile.
      setAllDependents(snap.docs.map(function (d) { var data = d.data(); return { uid: data.linkedUid || d.id, displayName: data.displayName }; }));
    });
  }, [occKey, template.id]);

  function nameFor(uid) {
    var v = voters.find(function (x) { return x.uid === uid; });
    if (v) return v.displayName;
    var u = allUsers.find(function (x) { return x.uid === uid; });
    if (u) return u.displayName;
    var dep = allDependents.find(function (x) { return x.uid === uid; });
    return dep ? dep.displayName : uid;
  }

  function startSession() {
    var playerIds = Object.keys(roster).filter(function (uid) { return roster[uid]; });
    if (playerIds.length < 4) { window.alert("Need at least 4 players — matches are always doubles."); return; }
    var playerNames = {};
    playerIds.forEach(function (uid) { playerNames[uid] = nameFor(uid); });
    setDoc(doc(db, "liveSessions", sessionId), {
      templateId: template.id,
      venueId: template.venueId,
      venueName: template.venueName,
      label: formatDayTime(template.dayOfWeek, template.time, template.endTime) + " — " + template.venueName,
      occurrenceDate: occKey,
      status: "live",
      mode: mode,
      courtCount: parseInt(courtCount, 10),
      organiserId: user.uid,
      organiserName: user.displayName,
      players: playerIds,
      playerNames: playerNames,
      round: 0,
      createdAt: new Date().toISOString(),
    });
  }

  function completeSession() {
    if (window.confirm("Mark this session as complete?")) {
      updateDoc(doc(db, "liveSessions", sessionId), { status: "complete" });
    }
  }

  function cancelSession() {
    var hasResults = live && ((live.currentRound && live.currentRound.courts.some(function (c) { return c.completed; }))
      || (live.groups && live.groups.some(function (g) { return g.results && g.results.length > 0; })));
    var msg = hasResults
      ? "Cancel and restart this session? Any scores already submitted stay recorded in Stats — this just resets the court setup so you can start over."
      : "Cancel this session? You'll go back to the setup screen.";
    if (window.confirm(msg)) {
      deleteDoc(doc(db, "liveSessions", sessionId));
    }
  }

  if (live === undefined) return <p style={{ fontSize: 13, color: colors.textDim }}>Loading…</p>;

  if (live && live.status === "live") {
    return (
      <div>
        <SessionHeader template={template} occurrenceDate={occurrenceDate} live={live} onExit={onExit} onComplete={completeSession} onCancel={cancelSession} />
        {live.mode === "social"
          ? <SocialSession sessionId={sessionId} live={live} />
          : <LeagueSession sessionId={sessionId} live={live} />}
      </div>
    );
  }

  if (live && live.status === "complete") {
    return (
      <div>
        <SessionHeader template={template} live={live} onExit={onExit} />
        <div style={cardStyle}>
          <p style={{ margin: 0 }}>This session has been marked complete. Check Stats for results.</p>
        </div>
      </div>
    );
  }

  // Not created yet -> setup + roster form
  var candidateIds = Array.from(new Set(voters.map(function (v) { return v.uid; }).concat(Object.keys(roster))));
  var byUid = {};
  allUsers.filter(function (u) { return u.active !== false; }).forEach(function (u) { byUid[u.uid] = u; });
  allDependents.forEach(function (d) { if (!byUid[d.uid]) byUid[d.uid] = d; });
  var availableToAdd = Object.keys(byUid).map(function (uid) { return byUid[uid]; })
    .filter(function (u) { return roster[u.uid] === undefined; });

  return (
    <div>
      <SessionHeader template={template} occurrenceDate={occurrenceDate} onExit={onExit} />
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <h3 style={{ marginTop: 0, fontSize: 15 }}>Set Up Session</h3>
        <div style={{ marginBottom: 14 }}>
          <div style={labelStyle}>Mode</div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={function () { setMode("social"); }} style={mode === "social" ? btnStyle : btnSecondaryStyle}>Social</button>
            <button onClick={function () { setMode("league"); }} style={mode === "league" ? btnStyle : btnSecondaryStyle}>League</button>
          </div>
        </div>
        <div>
          <div style={labelStyle}>Number of Courts</div>
          <input style={inputStyle} type="number" min="1" max="12" value={courtCount} onChange={function (e) { setCourtCount(e.target.value); }} />
        </div>
      </div>

      <div style={cardStyle}>
        <h3 style={{ marginTop: 0, fontSize: 15 }}>Who's Here Tonight?</h3>
        <p style={{ fontSize: 12, color: colors.textDim, marginTop: 0 }}>Pre-ticked from this week's poll votes — untick anyone who didn't show, add anyone who did.</p>
        {candidateIds.map(function (uid) {
          return (
            <label key={uid} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", fontSize: 14 }}>
              <input type="checkbox" checked={!!roster[uid]} onChange={function (e) {
                setRoster(function (prev) { return { ...prev, [uid]: e.target.checked }; });
              }} />
              {nameFor(uid)}
            </label>
          );
        })}
        {availableToAdd.length > 0 && (
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <select style={inputStyle} value={addUid} onChange={function (e) { setAddUid(e.target.value); }}>
              <option value="">+ Add someone else…</option>
              {availableToAdd.map(function (u) { return <option key={u.uid} value={u.uid}>{u.displayName}</option>; })}
            </select>
            <button
              style={{ ...btnSecondaryStyle, width: "auto", padding: "0 16px" }}
              onClick={function () {
                if (!addUid) return;
                setRoster(function (prev) { return { ...prev, [addUid]: true }; });
                setAddUid("");
              }}
            >Add</button>
          </div>
        )}
        <button style={{ ...btnStyle, marginTop: 16 }} onClick={startSession}>
          {"Start " + (mode === "social" ? "Social" : "League") + " Session"}
        </button>
      </div>
    </div>
  );
}

function SessionHeader({ template, occurrenceDate, live, onExit, onComplete, onCancel }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
      <div>
        <button onClick={onExit} style={{ background: "none", border: "none", color: colors.accent, cursor: "pointer", fontSize: 13, padding: 0, marginBottom: 6 }}>{"← Back"}</button>
        <h2 style={{ margin: 0, fontSize: 18 }}>{formatDayTime(template.dayOfWeek, template.time, template.endTime)}</h2>
        <div style={{ fontSize: 13, color: colors.textDim }}>
          {template.venueName + " · " + formatDate(occurrenceDate)}
          {live ? " · organised by " + live.organiserName : ""}
        </div>
      </div>
      {live && live.status === "live" && (onComplete || onCancel) && (
        <div style={{ display: "flex", gap: 8 }}>
          {onCancel && (
            <button onClick={onCancel} style={{ ...btnSecondaryStyle, width: "auto", padding: "8px 12px", fontSize: 12, color: colors.danger, borderColor: colors.dangerBorder }}>
              Cancel / Restart
            </button>
          )}
          {onComplete && (
            <button onClick={onComplete} style={{ ...btnSecondaryStyle, width: "auto", padding: "8px 12px", fontSize: 12 }}>End Session</button>
          )}
        </div>
      )}
    </div>
  );
}
